// Server-recorded activity (P13) → the reference's task log entries. Activity arrives through the durable pull
// feed (entity_type `activity`, permission-scoped like tasks), so colleagues' edits and @mentions appear on every
// device. Entries keep the server id (`aid`) for idempotent merging; local provisional entries (no `aid`) cover
// my unsent operations only and are dropped once the server has accepted them.
import type { LogChange, LogEntry } from '@/domain/model';
import type { ListRecord, TaskRecord } from './diff';
import { fileEditedChange } from '@/modules/markdown/activity';

export interface ServerActivity {
  id: string; project_id: string; task_id: string | null; actor_id: string | null; kind: string;
  changes: Record<string, unknown>; created_at: string; group?: string | null;
}
type Fmt = { date: (iso: string) => string; listTitle: (id: string) => string | null; userName: (id: string) => string };

const s = (v: unknown) => (typeof v === 'string' ? v : '');
const ba = (v: unknown): { b: unknown; a: unknown } => {
  if (v && typeof v === 'object' && 'before' in v) { const o = v as { before?: unknown; after?: unknown }; return { b: o.before, a: o.after }; }
  return { b: undefined, a: v };
};
const box = (done: boolean, text: string) => (text ? (done ? '☑ ' : '☐ ') + text : '');

/** Maps one activity row to zero or more log entries (an edit plus one entry per new @mention). */
export function activityToLog(a: ServerActivity, task: TaskRecord, fmt: Fmt): LogEntry[] {
  const by = a.actor_id || '', at = Date.parse(a.created_at), c = a.changes || {};
  const base = { by, at, aid: a.id, ...(a.group ? { sid: 'g:' + a.group } : {}) };
  const edit = (changes: LogChange[]): LogEntry => ({ ...base, k: 'edit', changes, text: 'edited ' + [...new Set(changes.map(x => x.label.toLowerCase()))].join(', ') });
  const item = (id: unknown) => (task.checklist || []).find(x => x.id === id);
  const out: LogEntry[] = [];
  switch (a.kind) {
    case 'task.created': out.push({ ...base, k: 'create', text: 'created this task' }); break;
    case 'task.completed': out.push({ ...base, k: 'done', text: 'completed' }); break;
    case 'task.reopened': out.push({ ...base, k: 'done', text: 'reopened' }); break;
    case 'task.restored': out.push({ ...base, k: 'restore', text: 'restored this task' }); break;
    case 'task.commented': out.push({ ...base, k: 'comment', text: 'commented' }); break; // v7: the comment itself is on the task
    case 'task.moved': {
      const t = fmt.listTitle(s(ba(c.list_id).a));
      out.push({ ...base, k: 'move', text: t === null ? 'moved this task' : 'moved to ' + (t ? '“' + t + '”' : 'the top list') });
      break;
    }
    case 'task.edited': {
      const ch: LogChange[] = [];
      if ('title' in c) { const v = ba(c.title); ch.push({ f: 'title', label: 'Title', b: s(v.b), a: s(v.a) }); }
      if ('notes' in c) { const v = ba(c.notes); ch.push({ f: 'notes', label: 'Notes', b: s(v.b), a: s(v.a) }); }
      if ('deadline' in c) { const v = ba(c.deadline); ch.push({ f: 'deadline', label: 'Deadline', b: v.b ? fmt.date(s(v.b)) : '', a: v.a ? fmt.date(s(v.a)) : '' }); }
      if (ch.length) out.push(edit(ch));
      break;
    }
    case 'task.tag_added': out.push(edit([{ f: 'tags', label: 'Tags', b: '', a: s(c.tag) }])); break;
    case 'task.tag_removed': out.push(edit([{ f: 'tags', label: 'Tags', b: s(c.tag), a: '' }])); break;
    // design v8
    case 'task.tag_renamed': out.push({ ...base, k: 'edit', text: 'renamed tag ' + s(c.from) + ' → ' + s(c.to) }); break;
    case 'task.assigned': case 'task.unassigned': {
      const u = s(c.user_id);
      out.push({ ...base, k: 'assign', text: (a.kind === 'task.assigned' ? 'assigned ' : 'unassigned ') + (u && u === by ? 'themselves' : fmt.userName(u)) });
      break;
    }
    case 'checklist.added': out.push(edit([{ f: 'cl:' + s(c.item_id), label: 'Checklist', b: '', a: box(!!c.done, s(c.text)) }])); break;
    case 'checklist.deleted': out.push(edit([{ f: 'cl:' + s(c.item_id), label: 'Checklist', b: box(false, s(c.text)), a: '' }])); break;
    case 'checklist.edited': { const v = ba(c.text), d = !!item(c.item_id)?.done; out.push(edit([{ f: 'cl:' + s(c.item_id), label: 'Checklist', b: box(d, s(v.b)), a: box(d, s(v.a)) }])); break; }
    case 'checklist.done': { const v = ba(c.done), text = item(c.item_id)?.text || ''; if (text) out.push(edit([{ f: 'cl:' + s(c.item_id), label: 'Checklist', b: box(!!v.b, text), a: box(!!v.a, text) }])); break; }
    case 'file_attached': case 'file_removed': {
      const name = s((c.file as { name?: unknown } | undefined)?.name), img = c.role === 'note_image' || (c.file as { role?: unknown } | undefined)?.role === 'note_image';
      if (name) out.push(edit([{ f: img ? 'images' : 'files', label: img ? 'Images' : 'Files', b: a.kind === 'file_removed' ? name : '', a: a.kind === 'file_attached' ? name : '' }]));
      break;
    }
    case 'file_edited': { const ch = fileEditedChange(c); if (ch) out.push(edit([ch])); break; } // v6 F14: one per new Markdown version
    default: break; // system/project-level kinds are not part of a task's history
  }
  const mentions = Array.isArray(c.mentions) ? c.mentions.filter((x): x is string => typeof x === 'string') : [];
  for (const to of mentions) out.push({ ...base, aid: a.id + ':' + to, k: 'mention', to, text: 'mentioned ' + fmt.userName(to) + (a.kind === 'task.commented' ? ' in a comment' : '') });
  return out;
}

/**
 * Reference grouping: consecutive edit entries by the same person in the same card session (`sid`), or within 30 s
 * without a session, show as one entry with merged field changes (first before → last after).
 */
export function groupLog(entries: LogEntry[]): LogEntry[] {
  const out: LogEntry[] = [];
  for (const e of [...entries].sort((x, y) => x.at - y.at)) {
    const prev = out[out.length - 1];
    if (e.k === 'edit' && prev && prev.k === 'edit' && prev.by === e.by && (e.sid ? prev.sid === e.sid : !prev.sid && e.at - prev.at < 30_000)) {
      const merged: LogChange[] = (prev.changes || []).map(x => ({ ...x }));
      for (const c of e.changes || []) { const ex = merged.find(x => x.f === c.f); if (ex) ex.a = c.a; else merged.push({ ...c }); }
      const changes = merged.filter(x => x.a !== x.b);
      out[out.length - 1] = { ...prev, at: e.at, changes, text: 'edited ' + [...new Set(changes.map(x => x.label.toLowerCase()))].join(', ') };
      if (!changes.length) out.pop();
      continue;
    }
    out.push(e);
  }
  return out;
}

/** Adds server entries to the raw server log (idempotent by `aid`), oldest first. */
export function mergeRaw(raw: LogEntry[] | undefined, incoming: LogEntry[]): LogEntry[] {
  const byAid = new Map<string, LogEntry>();
  for (const e of raw || []) if (e.aid) byAid.set(e.aid, e);
  for (const e of incoming) if (e.aid) byAid.set(e.aid, e);
  return [...byAid.values()].sort((x, y) => x.at - y.at);
}

/**
 * The displayed log: grouped server entries, plus synthetic create/done entries the server has not confirmed yet,
 * plus provisional local entries only while the task still has unsent operations.
 */
export function composeLog(raw: LogEntry[] | undefined, current: LogEntry[] | undefined, hasPending: boolean): LogEntry[] {
  const server = raw || [];
  const local = (current || []).filter(e => !e.aid);
  const synthetic = local.filter(e => e.syn && !server.some(x => x.k === e.k && (e.k !== 'done' || x.text === e.text)));
  const provisional = hasPending ? local.filter(e => !e.syn) : [];
  return [...groupLog(server), ...synthetic, ...provisional].sort((x, y) => x.at - y.at);
}

export type { ListRecord };
